import React, { useEffect, useState } from "react";
import {
  Alert, Autocomplete, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, InputAdornment, MenuItem, Snackbar, TextField,
} from "@mui/material";
import { FichaContenedor, FilaFicha } from "../../solicitudCheque/components/FichaSolicitud";
import {
  agregarLinea, buscarProveedoresOC, catalogoEquiposOC, catalogoModelosOC, catalogoTiposOC, editarLinea, mensajeError, simbolo,
} from "../services/ordenCompraService";

const vacio = () => ({ cantidad: "1", servicio: "", servicioId: "", equipo: "", tipo: "", modelo: "", marca: "", tipo_compra: "Activo", proveedor: "", vida_util: "", precio: "", observacion: "" });

// Agregar o modificar una línea de compra. El equipo se elige en cascada: servicio › tipo de equipo › tipo de modelo › modelo (catálogo de equipos).
export default function LineaFormDialog({ open, ordenId, moneda, linea, catalogos, proveedorOrden, onClose, onGuardada }) {
  const editando = !!linea;
  const [form, setForm] = useState(vacio());
  const [cambiarCatalogo, setCambiarCatalogo] = useState(true);
  const [equipos, setEquipos] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [modelos, setModelos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    setEquipos([]); setTipos([]); setModelos([]);
    if (linea) {
      setCambiarCatalogo(false);
      setForm({ ...vacio(), cantidad: String(linea.cantidad), marca: linea.marca, tipo_compra: linea.tipo_compra, proveedor: linea.proveedor, vida_util: linea.vida_util, precio: String(linea.precio), observacion: linea.observacion });
    } else {
      setCambiarCatalogo(true);
      setForm({ ...vacio(), proveedor: proveedorOrden || "" });
    }
  }, [open, linea, proveedorOrden]);

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const elegirServicio = async (servicioId) => {
    // AVL y Scada comparten la misma relación (2): el select se identifica por el id del servicio y a la API se le manda su relación.
    const elegido = (catalogos?.servicios || []).find((x) => String(x.id) === String(servicioId));
    const servicio = elegido ? String(elegido.relacion) : "";
    setForm((f) => ({ ...f, servicioId, servicio, equipo: "", tipo: "", modelo: "" }));
    setEquipos([]); setTipos([]); setModelos([]);
    if (!servicio) return;
    try { const { data } = await catalogoEquiposOC(servicio); setEquipos(data); } catch { setEquipos([]); }
  };
  const elegirEquipo = async (equipo) => {
    setForm((f) => ({ ...f, equipo, tipo: "", modelo: "" }));
    setTipos([]); setModelos([]);
    if (!equipo) return;
    try { const { data } = await catalogoTiposOC(equipo); setTipos(data); } catch { setTipos([]); }
  };
  const elegirTipo = async (tipo) => {
    setForm((f) => ({ ...f, tipo, modelo: "" }));
    setModelos([]);
    if (!tipo) return;
    try { const { data } = await catalogoModelosOC(tipo); setModelos(data); } catch { setModelos([]); }
  };
  const buscarProveedor = async (texto) => {
    try { const { data } = await buscarProveedoresOC(texto); setProveedores(Array.isArray(data) ? data : []); } catch { setProveedores([]); }
  };

  const guardar = async (e) => {
    e.preventDefault();
    if (cambiarCatalogo && (!form.servicio || !form.equipo || !form.tipo || !form.modelo)) return setError("Elige el servicio, el tipo de equipo, el tipo de modelo y el modelo.");
    if (!(Number(form.cantidad) > 0)) return setError("La cantidad debe ser mayor a 0.");
    if (String(form.precio).trim() === "") return setError("Indica el precio unitario.");
    setGuardando(true);
    setError("");
    try {
      if (editando) await editarLinea(ordenId, linea.id, { ...form, cambiar_catalogo: cambiarCatalogo });
      else await agregarLinea(ordenId, form);
      onGuardada();
    } catch (err) {
      setError(mensajeError(err, "No se pudo guardar la línea."));
    } finally {
      setGuardando(false);
    }
  };

  const totalLinea = (Number(String(form.cantidad).replace(",", ".")) || 0) * (Number(String(form.precio).replace(",", ".")) || 0);
  
  return (
    <Dialog open={open} onClose={guardando ? undefined : onClose} maxWidth="md" fullWidth component="form" onSubmit={guardar} scroll="paper">
      <DialogTitle sx={{ fontSize: 18, fontWeight: 800, py: 1.25 }}>{editando ? "Modificar línea de compra" : "Agregar línea de compra"}</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "background.default", p: 1.5, display: "grid", gap: 1 }}>
        {editando && (
          <FichaContenedor>
            <FilaFicha campos={[{ etiqueta: "Equipo actual", valor: `${linea.servicio_nombre} › ${linea.equipo} › ${linea.detalle}` }]} />
            <FilaFicha campos={[{ etiqueta: "Cambiar equipo", control: <FormControlLabel sx={{ m: 0 }} control={<Checkbox size="small" checked={cambiarCatalogo} onChange={(e) => setCambiarCatalogo(e.target.checked)} />} label="Elegir otro servicio, tipo y modelo" /> }]} />
          </FichaContenedor>
        )}

        {cambiarCatalogo && (
          <FichaContenedor>
            <FilaFicha campos={[
              { etiqueta: "Servicio *", control: (
                <TextField fullWidth size="small" select hiddenLabel value={form.servicioId} onChange={(e) => elegirServicio(e.target.value)} disabled={false} slotProps={{ select: { displayEmpty: true }, htmlInput: { "aria-label": "Servicio" } }}>
                      <MenuItem value="" disabled>Elegir…</MenuItem>
                      {(catalogos?.servicios || []).map((x) => <MenuItem key={x.id} value={String(x.id)}>{x.nombre}</MenuItem>)}
                    </TextField>
              ) },
              { etiqueta: "Tipo de equipo *", control: (
                <TextField fullWidth size="small" select hiddenLabel value={form.equipo} onChange={(e) => elegirEquipo(e.target.value)} disabled={!equipos.length} slotProps={{ select: { displayEmpty: true }, htmlInput: { "aria-label": "Tipo de equipo" } }}>
                      <MenuItem value="" disabled>Elegir…</MenuItem>
                      {equipos.map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                    </TextField>
              ) },
            ]}
            />
            <FilaFicha campos={[
              { etiqueta: "Tipo de modelo *", control: (
                <TextField fullWidth size="small" select hiddenLabel value={form.tipo} onChange={(e) => elegirTipo(e.target.value)} disabled={!tipos.length} slotProps={{ select: { displayEmpty: true }, htmlInput: { "aria-label": "Tipo de modelo" } }}>
                      <MenuItem value="" disabled>Elegir…</MenuItem>
                      {tipos.map((x) => <MenuItem key={x.id} value={x.id}>{x.nombre}</MenuItem>)}
                    </TextField>
              ) },
              { etiqueta: "Modelo *", control: (
                <TextField fullWidth size="small" select hiddenLabel value={form.modelo} onChange={set("modelo")} disabled={!modelos.length} slotProps={{ select: { displayEmpty: true }, htmlInput: { "aria-label": "Modelo" } }}>
                      <MenuItem value="" disabled>Elegir…</MenuItem>
                      {modelos.map((x) => <MenuItem key={x.id} value={x.id}>{x.nombre}</MenuItem>)}
                    </TextField>
              ) },
            ]}
            />
            {form.equipo.trim().toLowerCase() === "accesorio" && (
              <Alert severity="info" sx={{ py: 0, m: 0.75 }}>Los accesorios se controlan solo por cantidad: no se registran por serie ni se importan desde Excel.</Alert>
            )}
          </FichaContenedor>
        )}

        <FichaContenedor>
          <FilaFicha campos={[
            { etiqueta: "Cantidad *", control: <TextField fullWidth size="small" hiddenLabel value={form.cantidad} onChange={set("cantidad")} slotProps={{ htmlInput: { inputMode: "decimal", "aria-label": "Cantidad" } }} /> },
            { etiqueta: "Precio unitario *", control: (
              <TextField
                fullWidth size="small" hiddenLabel value={form.precio} onChange={set("precio")} placeholder="0.00"
                slotProps={{ input: { startAdornment: <InputAdornment position="start">{simbolo(moneda)}</InputAdornment> }, htmlInput: { inputMode: "decimal", "aria-label": "Precio unitario" } }}
              />
            ) },
          ]}
          />
          <FilaFicha campos={[
            { etiqueta: "Tipo de compra", control: (
              <TextField fullWidth size="small" select hiddenLabel value={form.tipo_compra} onChange={set("tipo_compra")} slotProps={{ htmlInput: { "aria-label": "Tipo de compra" } }}>
                {(catalogos?.tipos_compra || []).map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
              </TextField>
            ) },
            { etiqueta: "Marca", control: <TextField fullWidth size="small" hiddenLabel value={form.marca} onChange={set("marca")} slotProps={{ htmlInput: { maxLength: 100, "aria-label": "Marca" } }} /> },
          ]}
          />
          <FilaFicha campos={[
            { etiqueta: "Proveedor", control: (
              <Autocomplete
                freeSolo fullWidth size="small" options={proveedores} value={form.proveedor} inputValue={form.proveedor} filterOptions={(x) => x}
                onFocus={() => buscarProveedor(form.proveedor)} onInputChange={(_, v) => { setForm((f) => ({ ...f, proveedor: v })); buscarProveedor(v); }}
                renderInput={(p) => <TextField {...p} hiddenLabel slotProps={{ htmlInput: { ...p.inputProps, maxLength: 100, "aria-label": "Proveedor" } }} />}
              />
            ) },
            { etiqueta: "Vida útil (años)", control: <TextField fullWidth size="small" hiddenLabel value={form.vida_util} onChange={set("vida_util")} slotProps={{ htmlInput: { inputMode: "numeric", "aria-label": "Años de vida útil" } }} /> },
          ]}
          />
          <FilaFicha campos={[{ etiqueta: "Observación", alto: true, control: <TextField fullWidth size="small" hiddenLabel multiline minRows={2} value={form.observacion} onChange={set("observacion")} slotProps={{ htmlInput: { "aria-label": "Observación" } }} /> }]} />
          <FilaFicha campos={[{ etiqueta: "Total de la línea", valor: totalLinea > 0 ? `${simbolo(moneda)} ${totalLinea.toLocaleString("es-BO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "" }]} />
        </FichaContenedor>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando} sx={{ textTransform: "none" }}>Cancelar</Button>
        <Button type="submit" variant="contained" disabled={guardando} sx={{ textTransform: "none", fontWeight: 700 }}>
          {guardando ? <CircularProgress size={20} color="inherit" /> : "Guardar"}
        </Button>
      </DialogActions>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={(_, m) => m !== "clickaway" && setError("")} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity="error" variant="filled" onClose={() => setError("")} sx={{ width: "100%" }}>{error}</Alert>
      </Snackbar>
    </Dialog>
  );
}
